import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { converterRegistry } from './utils/converterRegistry';
import { convertFileWithAPI, convertFileWithAPIExtended } from './utils/converterApi';
import { downloadCSV } from './utils/shared';
import { parseCSVContent, buildFilteredCsv } from './utils/csvParser';
import {
  AppHeader,
  ToolSelector,
  ConverterConfiguration,
  FileConverter,
  ImportModal,
  OverspendCoverageModal,
  ServerManagementModal,
  AuthModal,
  SubscriptionModal,
  BetaModeModal,
  AccountSettingsModal,
  OnboardingJourney,
  JourneyResumeBanner,
  useServerManagement,
  useImportFlow
} from './components';
import { useAuth } from './hooks/useAuth';
import { useOnboardingJourney } from './hooks/useOnboardingJourney';
import { useSubscription } from './hooks/useSubscription';
import { useAutoProvisionHostedServer } from './hooks/useAutoProvisionHostedServer';
import { auth } from './utils/firebase';
import { useAccountConfiguration, AccountConfiguration } from './hooks/useAccountConfiguration';
import { isFeatureEnabled } from './utils/featureFlags';
import {
  ConverterType,
  CurrencyType,
  ConversionResult,
  CategorizationConfig,
  NewServerForm,
  ServerConnectPrefill,
  API_BASE_URL,
  Account,
  PreviewTransaction
} from './types';
import { resolveImportAccountSelection } from './utils/importAccountSelection';
import { resolveConverterFromConfig } from './utils/converterResolution';

function App() {
  const { t } = useTranslation();
  
  // Authentication state
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  
  // Core converter state - start with no selection to force user to choose country/bank
  const [activeConverter, setActiveConverter] = useState<ConverterType>('');
  const [targetCurrency, setTargetCurrency] = useState<CurrencyType>('EUR');
  const [sourceCurrency, setSourceCurrency] = useState<CurrencyType>('EUR');
  const [startDate, setStartDate] = useState<string>('');
  const [ramiTargetCurrency, setRamiTargetCurrency] = useState<CurrencyType>('SAR');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [previewTransactions, setPreviewTransactions] = useState<PreviewTransaction[]>([]);
  const [csvHeader, setCsvHeader] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  // Categorization options
  const [enableCategorization, setEnableCategorization] = useState(false);
  const [categorizationConfig, setCategorizationConfig] = useState<CategorizationConfig | null>(null);

  // UI state
  const [showServerManagement, setShowServerManagement] = useState(false);
  const [serverConnectPrefill, setServerConnectPrefill] = useState<ServerConnectPrefill | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [betaModalFeature, setBetaModalFeature] = useState<string>('');
  const [showAccountSettingsModal, setShowAccountSettingsModal] = useState(false);
  const [selectedAccountForSettings, setSelectedAccountForSettings] = useState<Account | null>(null);
  const [newServerForm, setNewServerForm] = useState<NewServerForm>({ name: '', url: '', password: '' });
  const refreshInProgressRef = useRef(false);
  const lastRefreshTimeRef = useRef(0);
  const loadAccountsRef = useRef<(
    server: any,
    budgetId: string,
    budgetPassword?: string,
    preferredAccountId?: string | null
  ) => Promise<void>>(null);
  const preferredImportAccountIdRef = useRef<string | null>(null);

  // Authentication handlers
  const handleShowAuthModal = (mode: 'signin' | 'signup' = 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };

  // Subscription handlers
  const handleShowSubscriptionModal = () => {
    setShowSubscriptionModal(true);
  };

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false);
  };

  // Beta mode handlers
  const [pendingBetaAction, setPendingBetaAction] = useState<(() => void) | null>(null);

  const handleShowBetaModal = (featureName: string, onContinue?: () => void) => {
    setBetaModalFeature(featureName);
    setPendingBetaAction(() => onContinue);
    setShowBetaModal(true);
  };

  const handleCloseBetaModal = () => {
    setShowBetaModal(false);
    setBetaModalFeature('');
    setPendingBetaAction(null);
  };

  const handleContinueToFeature = () => {
    setShowBetaModal(false);
    setBetaModalFeature('');
    // Execute the pending action
    if (pendingBetaAction) {
      pendingBetaAction();
    }
    setPendingBetaAction(null);
  };

  // Initialize hooks
  const importFlowHook = useImportFlow();
  const serverManagement = useServerManagement();
  const accountConfig = useAccountConfiguration();
  const subscription = useSubscription();
  const { journey, updateJourney } = useOnboardingJourney();
  const hostedProvisioning = useAutoProvisionHostedServer(user);

  // Exchange rate cache is now handled by the API
  // No need for client-side cache initialization

  // Update target currency when active server changes or when no account is selected.
  // Use primitive deps only to avoid "Maximum update depth" (object/array refs change every render).
  const savedServersLength = serverManagement.savedServers?.length ?? 0;
  const activeServerId = serverManagement.activeServerId;
  const selectedAccountId = accountConfig.selectedAccountId;

  useEffect(() => {
    const activeServer = serverManagement.getActiveServer();
    const budgetCurrency = activeServer?.defaultBudgetCurrency as CurrencyType | undefined;

    if (!budgetCurrency || !selectedAccountId) {
      return;
    }

    // Keep conversion currency aligned with the budget when a linked account is selected
    setTargetCurrency(budgetCurrency);
    setRamiTargetCurrency(budgetCurrency);
    console.log(`🔄 [App] Using budget currency for conversion: ${budgetCurrency}`);
  }, [savedServersLength, activeServerId, selectedAccountId]);

  // Auto-configure categorization when active server changes.
  // Use primitive deps to avoid "Maximum update depth".
  useEffect(() => {
    const activeServer = serverManagement.getActiveServer();
    if (activeServer && activeServer.defaultBudgetId && activeServer.defaultBudgetName) {
      // Auto-configure categorization if server has default budget
      setCategorizationConfig({
        server: activeServer,
        budgetId: activeServer.defaultBudgetId,
        budgetPassword: activeServer.defaultBudgetPassword
      });
    } else {
      // Clear categorization config if no default budget
      setCategorizationConfig(null);
    }
  }, [savedServersLength, activeServerId]);

  // Open auth modal from URL query param (e.g. redirected from pricing page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auth = params.get('auth');
    if (auth === 'signup' || auth === 'signin') {
      setAuthMode(auth);
      setShowAuthModal(true);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Close auth modal when user is authenticated
  useEffect(() => {
    if (user && showAuthModal) {
      setShowAuthModal(false);
    }
  }, [user, showAuthModal]);

  // Load accounts for import with the hook's helper (with caching and background refresh)
  loadAccountsRef.current = async (
    server: any,
    budgetId: string,
    budgetPassword?: string,
    preferredAccountId?: string | null
  ) => {
    if (preferredAccountId !== undefined) {
      preferredImportAccountIdRef.current = preferredAccountId;
    }

    const resolvedPreferredAccountId = preferredImportAccountIdRef.current;

    // Validate inputs before making API call
    if (!server || !budgetId || !server.url || !server.password) {
      setError('Invalid server configuration');
      importFlowHook.updateImportFlow({ step: 'servers' });
      return;
    }

    try {
      // Check if we have cached accounts and show them immediately
      const cachedAccounts = server.cachedAccounts?.[budgetId];
      const cacheTime = server.accountsCachedAt?.[budgetId];
      const now = Date.now();
      const cacheMaxAge = 2 * 60 * 1000; // 2 minutes
      
      // If we have valid cached accounts, show them immediately
      if (cachedAccounts && cacheTime && (now - cacheTime) < cacheMaxAge) {
        const { selectedAccountId, accountPreselected } = resolveImportAccountSelection(
          cachedAccounts,
          resolvedPreferredAccountId,
          server.lastUsedAccountId
        );

        importFlowHook.updateImportFlow({
          accounts: cachedAccounts,
          selectedAccountId,
          accountPreselected,
          step: 'ready',
          isRefreshingAccounts: false
        });
        
        // Start background refresh after showing cached data
        setTimeout(() => {
          refreshAccountsInBackground(server, budgetId, cachedAccounts, budgetPassword);
        }, 100);
        return;
      }
      
      // If no cached data or expired, fetch fresh data
      const accounts = await serverManagement.loadAccountsForBudget(server, budgetId, budgetPassword, false, false);
      const { selectedAccountId, accountPreselected } = resolveImportAccountSelection(
        accounts,
        resolvedPreferredAccountId,
        server.lastUsedAccountId
      );
      
      // Batch the state update to prevent multiple re-renders
      importFlowHook.updateImportFlow({
        accounts,
        selectedAccountId,
        accountPreselected,
        step: 'ready',
        isRefreshingAccounts: false
      });
    } catch (err) {
      if (err instanceof Error && err.message === 'NEEDS_PASSWORD') {
        importFlowHook.updateImportFlow({ 
          step: 'budget-password',
          needsBudgetPassword: true,
          isRefreshingAccounts: false
        });
        setError(null);
        return;
      }
      console.error('Failed to load accounts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
      importFlowHook.updateImportFlow({ 
        step: 'budgets',
        isRefreshingAccounts: false
      });
    }
  };

  const loadAccountsForImport = loadAccountsRef.current;

  // Background refresh function for accounts
  const refreshAccountsInBackground = async (server: any, budgetId: string, currentAccounts: any[], budgetPassword?: string) => {
    const now = Date.now();
    
    // Prevent multiple concurrent refreshes
    if (refreshInProgressRef.current) {
      return;
    }
    
    // Debounce: prevent calls within 5 seconds of each other
    if (now - lastRefreshTimeRef.current < 5000) {
      return;
    }
    
    refreshInProgressRef.current = true;
    lastRefreshTimeRef.current = now;
    
    try {
      // Set refreshing indicator
      importFlowHook.updateImportFlow({ isRefreshingAccounts: true });
      
      const freshAccounts = await serverManagement.loadAccountsForBudget(server, budgetId, budgetPassword, true, false);
      
      // Get current import flow state
      const currentFlow = importFlowHook.importFlow;
      
      // Only update if there are actual changes and user hasn't clicked import
      if (!currentFlow.hasClickedImport && !arraysEqual(currentAccounts, freshAccounts)) {
        const { selectedAccountId, accountPreselected } = resolveImportAccountSelection(
          freshAccounts,
          preferredImportAccountIdRef.current,
          server.lastUsedAccountId
        );

        importFlowHook.updateImportFlow({
          accounts: freshAccounts,
          selectedAccountId: currentFlow.selectedAccountId || selectedAccountId,
          accountPreselected: currentFlow.accountPreselected || accountPreselected,
          isRefreshingAccounts: false
        });
      } else {
        // Clear refreshing indicator even if no updates needed
        importFlowHook.updateImportFlow({ isRefreshingAccounts: false });
      }
    } catch (error) {
      console.warn('Background refresh of accounts failed:', error);
      // Clear refreshing indicator on error
      importFlowHook.updateImportFlow({ isRefreshingAccounts: false });
    } finally {
      refreshInProgressRef.current = false;
    }
  };

  // Helper function to compare arrays of accounts
  const arraysEqual = (a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    
    // Sort both arrays by id for consistent comparison
    const sortedA = [...a].sort((x, y) => x.id.localeCompare(y.id));
    const sortedB = [...b].sort((x, y) => x.id.localeCompare(y.id));
    
    return sortedA.every((item, index) => {
      const otherItem = sortedB[index];
      return item.id === otherItem.id && 
             item.name === otherItem.name && 
             item.type === otherItem.type && 
             item.closed === otherItem.closed;
    });
  };

  // Handle server selection in import flow
  const handleServerSelection = async (server: any) => {
    importFlowHook.updateImportFlow({ selectedServer: server, step: 'budgets' });
    setIsLoading(true);
    setError(null);

    try {
      const budgets = await serverManagement.loadBudgetsForServer(server);
      importFlowHook.updateImportFlow({
        budgets: budgets,
        selectedBudgetId: server.lastUsedBudgetId || budgets[0]?.id
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to Actual server');
      importFlowHook.updateImportFlow({ step: 'servers' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle budget selection
  const handleBudgetSelection = async (budgetId: string, budgetPassword?: string) => {
    if (!importFlowHook.importFlow.selectedServer) return;

    importFlowHook.updateImportFlow({ selectedBudgetId: budgetId, step: 'accounts' });
    await loadAccountsForImport(importFlowHook.importFlow.selectedServer, budgetId, budgetPassword);
  };

  // Handle new server creation (simplified)
  const handleCreateServer = async () => {
    if (!newServerForm.name.trim() || !newServerForm.url.trim() || !newServerForm.password.trim()) {
      setError(t('pleaseFillInAllDetails', { ns: 'serverManagement' }));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get authentication token
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error(t('authRequiredForServerManagement', { ns: 'auth' }));
      }

      // Test connection first
      let response;
      try {
        response = await fetch(`${API_BASE_URL}/auth-and-get-budgets`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            serverURL: newServerForm.url,
            password: newServerForm.password
          }),
        });
      } catch (networkError) {
        throw new Error(`${t('networkError', { ns: 'serverManagement' })}: ${networkError instanceof Error ? networkError.message : t('failedToConnect', { ns: 'serverManagement' })}`);
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        throw new Error(errorData.error || t('failedToAuthenticate', { ns: 'serverManagement' }));
      }

      const data = await response.json();
      
      // Create and save new server
      const newServer = {
        id: Date.now().toString(),
        name: newServerForm.name,
        url: newServerForm.url,
        password: newServerForm.password,
        cachedBudgets: data.budgets || [],
        budgetsCachedAt: Date.now()
      };
      
      const updatedServers = [...serverManagement.savedServers, newServer];
      serverManagement.saveServers(updatedServers);
      serverManagement.setActiveServer(newServer.id);

      // Reset form
      setNewServerForm({ name: '', url: '', password: '' });
      
      // Update import flow to use new server
      importFlowHook.updateImportFlow({
        selectedServer: newServer,
        budgets: data.budgets || [],
        selectedBudgetId: data.budgets?.[0]?.id,
        step: 'budgets'
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToConnect', { ns: 'serverManagement' }));
    } finally {
      setIsLoading(false);
    }
  };

  // Process file for conversion
  const processFile = async (file: File) => {
    // Validate file type based on active converter
    if (activeConverter === 'alrajhi') {
      if (!file.name.toLowerCase().endsWith('.xls') && !file.name.toLowerCase().endsWith('.xlsx')) {
        setError('Please select a valid Excel file (.xls or .xlsx)');
        return;
      }
    } else if (activeConverter === 'wise' || activeConverter === 'cib' || activeConverter === 'category' || activeConverter === 'other') {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError('Please select a valid CSV file (.csv)');
        return;
      }
    }

    // Validate category converter specific requirements
    if (activeConverter === 'category') {
      if (!startDate) {
        setError('Please select a start date for category filtering');
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      let csvContent: string;
      let filename: string;
      let bankName: string | undefined;

      // Prepare categorization config for generateCSV if enabled
      let budgetConfig = undefined;
      if (enableCategorization && categorizationConfig?.server && categorizationConfig?.budgetId) {
        budgetConfig = {
          serverURL: categorizationConfig.server.url,
          password: categorizationConfig.server.password,
          budgetId: categorizationConfig.budgetId,
          budgetPassword: categorizationConfig.budgetPassword
        };
      }

      // Handle "other" converter specially (no converter registry needed)
      if (activeConverter === 'other') {
        // Call API directly without bankId for auto-detection
        const apiResponse = await convertFileWithAPIExtended(file, undefined, targetCurrency, startDate, enableCategorization, budgetConfig);
        csvContent = apiResponse.csvContent;
        bankName = apiResponse.bankName;
        filename = file.name.replace(/\.csv$/i, `_auto_detected_${targetCurrency.toLowerCase()}${enableCategorization ? '_categorized' : ''}.csv`);
      } else {
        // Load converter definition on-demand for other converters
        const converterDef = await converterRegistry.loadConverterConfig(activeConverter);
        if (!converterDef) {
          throw new Error(`Unknown converter type: ${activeConverter}`);
        }

        // Call converter with appropriate parameters based on function name
        if (converterDef.converterFunction === 'convertCSVWithConverterConfig') {
          // Use API for universal conversion (with or without categorization)
          csvContent = await convertFileWithAPI(file, converterDef.id, targetCurrency, startDate, enableCategorization, budgetConfig);
          filename = file.name.replace(/\.(csv|xls|xlsx)$/i, `_${converterDef.id}_${targetCurrency.toLowerCase()}${enableCategorization ? '_categorized' : ''}.csv`);
        } else if (converterDef.converterFunction === 'convertCategoryCsv') {
          // Get converter function from registry for local converters
          const converterFunction = converterRegistry.getConverterFunction(converterDef.converterFunction);
          if (!converterFunction) {
            throw new Error(`Converter function not found: ${converterDef.converterFunction}`);
          }
          csvContent = await converterFunction(file, sourceCurrency, ramiTargetCurrency, startDate, enableCategorization, budgetConfig);
          filename = file.name.replace(/\.csv$/i, `_nimah_${sourceCurrency.toLowerCase()}_to_${ramiTargetCurrency.toLowerCase()}${enableCategorization ? '_categorized' : ''}.csv`);
        } else {
          throw new Error(`Unknown converter function: ${converterDef.converterFunction}`);
        }
      }

      const transactionCount = csvContent.split('\n').length - 1; // Subtract header row
      const { header, transactions } = parseCSVContent(csvContent);

      setCsvHeader(header);
      setPreviewTransactions(transactions);
      setResult({
        csvContent,
        transactionCount,
        filename,
        bankName
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while converting the file');
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedCsvContent = () => buildFilteredCsv(csvHeader, previewTransactions);

  const handleDownload = () => {
    if (result) {
      downloadCSV(getSelectedCsvContent(), result.filename);
    }
  };

  const handleReset = () => {
    setResult(null);
    setPreviewTransactions([]);
    setCsvHeader('');
    setError(null);
    setIsDragOver(false);
    importFlowHook.resetImportFlow();
    // Reset file input
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    // Do NOT deselect account here – "Convert another" should keep the current account.
    // User can clear selection via the "Clear selection" button in the accounts section.
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set to false if we're leaving the upload area entirely
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      await processFile(file);
    }
  };

  // Handle account selection and restore configuration
  const handleAccountSelect = (account: Account, config: AccountConfiguration) => {
    console.log(`🎯 [App] Account selected: ${account.name} - applying configuration`);
    
    const activeServer = serverManagement.getActiveServer();
    const budgetCurrency = activeServer?.defaultBudgetCurrency as CurrencyType | undefined;
    
    // Check if the account has configuration
    const hasConfiguration = config.selectedCountry || 
                            config.selectedBank || 
                            config.activeConverter;
    
    // If account has no configuration and modal is not already open for this account, open the settings modal
    if (!hasConfiguration && (!showAccountSettingsModal || selectedAccountForSettings?.id !== account.id)) {
      setSelectedAccountForSettings(account);
      setShowAccountSettingsModal(true);
    }
    
    // Apply the cached configuration; budget currency always wins over stored target currency
    const resolvedConverter = resolveConverterFromConfig(config);
    setActiveConverter(resolvedConverter);
    setTargetCurrency(budgetCurrency || config.targetCurrency);
    setSourceCurrency(config.sourceCurrency);
    setRamiTargetCurrency(budgetCurrency || config.ramiTargetCurrency);
    setStartDate(config.startDate);
    setEnableCategorization(config.enableCategorization);

    if (!config.activeConverter && resolvedConverter) {
      accountConfig.updateAccountConfiguration({ activeConverter: resolvedConverter });
    }
    
    console.log(`✅ [App] Configuration restored for ${account.name}:`, {
      country: config.selectedCountry,
      bank: config.selectedBank,
      converter: resolvedConverter,
      targetCurrency: budgetCurrency || config.targetCurrency
    });
  };

  // Handle account deselection and reset converter state for manual country/bank flow
  const handleAccountDeselect = () => {
    console.log('🔚 [App] Account deselected - resetting converter state');
    accountConfig.deselectAccount();
    setActiveConverter('');
    setResult(null);
    setPreviewTransactions([]);
    setCsvHeader('');
    setError(null);
    setStartDate('');
    setIsDragOver(false);
    importFlowHook.resetImportFlow();
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };



  // Save configuration whenever settings change. Use selectedAccountId (primitive) to avoid loop.
  useEffect(() => {
    if (selectedAccountId) {
      accountConfig.updateAccountConfiguration({
        activeConverter,
        targetCurrency,
        sourceCurrency,
        ramiTargetCurrency,
        startDate,
        enableCategorization,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit accountConfig to avoid loop (it changes when query updates)
  }, [activeConverter, targetCurrency, sourceCurrency, ramiTargetCurrency, startDate, enableCategorization, selectedAccountId]);

  // Reset when switching converters
  const handleConverterChange = (converter: ConverterType) => {
    setActiveConverter(converter);
    handleReset();
    // Reset universal date state
    setStartDate('');
    // Save the converter change to account config
    if (accountConfig.selectedAccountId) {
      accountConfig.updateAccountConfiguration({ activeConverter: converter });
    }
  };

  const handleShowAccountSettings = (account: Account) => {
    setSelectedAccountForSettings(account);
    setShowAccountSettingsModal(true);
  };

  const handleCloseAccountSettingsModal = () => {
    setShowAccountSettingsModal(false);
    setSelectedAccountForSettings(null);
  };

  const handleUnlinkAccount = (account: Account) => {
    // The unlink functionality is handled in the modal
    // This is just a callback for any additional cleanup if needed
    console.log('Account unlinked:', account.name);
  };

  const handleToggleTransaction = (id: string) => {
    setPreviewTransactions((current) =>
      current.map((transaction) =>
        transaction.id === id
          ? { ...transaction, selected: !transaction.selected }
          : transaction
      )
    );
  };

  const handleSelectAllTransactions = (selected: boolean) => {
    setPreviewTransactions((current) =>
      current.map((transaction) => ({ ...transaction, selected }))
    );
  };

  const selectedTransactionCount = previewTransactions.filter(
    (transaction) => transaction.selected
  ).length;

  const effectiveTargetCurrency =
    activeConverter === 'category' ? ramiTargetCurrency : targetCurrency;

  const startImportFlow = () => {
    if (!user) {
      handleShowAuthModal('signup');
      return;
    }

    if (selectedTransactionCount === 0) {
      return;
    }

    const preferredAccountId = accountConfig.selectedAccountId;
    
    // Check if paywall is enabled
    if (isFeatureEnabled('ENABLE_PAYWALL')) {
      // Check subscription for Actual Budget import features
      if (!subscription.hasActiveSubscription) {
        handleShowSubscriptionModal();
        return;
      }
    } else {
      // Show beta modal instead of subscription modal
      handleShowBetaModal(t('actualBudgetImport', { ns: 'features' }), () => {
        const activeServer = serverManagement.getActiveServer();
        importFlowHook.startImportFlow(activeServer, loadAccountsRef.current!, preferredAccountId);
      });
      return;
    }
    
    const activeServer = serverManagement.getActiveServer();
    importFlowHook.startImportFlow(activeServer, loadAccountsRef.current!, preferredAccountId);
  };

  const executeImport = () => {
    if (!result || selectedTransactionCount === 0) return;
    // Set flag to prevent view updates while importing
    importFlowHook.updateImportFlow({ hasClickedImport: true });
    importFlowHook.executeImport(getSelectedCsvContent(), setError, serverManagement.saveLastUsedConfig);
  };

  // Leave the guided journey and reveal the free tools (converter)
  const handleExploreTools = () => {
    updateJourney({ status: 'explored' });
  };

  // The onboarding journey takes over the page while the user is choosing a
  // path (first visit) or following the guided setup. Signed-in users with
  // not-started must still see the hero — requiring !user hid it on nimah.app
  // after Firebase session restore.
  const isJourneyVisible =
    journey.status === 'guided' ||
    (journey.status === 'not-started' && serverManagement.savedServers.length === 0);

  // Show the resume/start banner on the converter homepage whenever onboarding
  // was never finished. Do not require a missing defaultBudgetId — hosted
  // auto-provision can connect a budget before the guided path is done.
  const showResumeBanner = journey.status !== 'completed';

  return (
    <div className="App">
      <AppHeader
        user={user}
        activeServer={serverManagement.getActiveServer()}
        onShowAuthModal={handleShowAuthModal}
        currentPage="home"
        onManageServers={() => {
          if (isFeatureEnabled('ENABLE_PAYWALL')) {
            if (!subscription.hasActiveSubscription) {
              handleShowSubscriptionModal();
              return;
            }
          } else {
            handleShowBetaModal(t('serverManagement', { ns: 'features' }), () => {
              setShowServerManagement(true);
            });
            return;
          }

          setShowServerManagement(true);
        }}
      />

      <main className="App-main">
        {/* Onboarding journey for first-time visitors and guided setup */}
        {isJourneyVisible && (
          <OnboardingJourney
            user={user}
            activeServer={serverManagement.getActiveServer()}
            savedServers={serverManagement.savedServers}
            journey={journey}
            updateJourney={updateJourney}
            onShowAuthModal={handleShowAuthModal}
            onShowServerManagement={() => {
              setServerConnectPrefill(null);
              setShowServerManagement(true);
            }}
            onExploreTools={handleExploreTools}
            onSaveServers={serverManagement.saveServers}
            onSetActiveServer={serverManagement.setActiveServer}
            onValidateBudgetAccess={serverManagement.validateBudgetAccess}
            hostingEnabled={hostedProvisioning.hostingEnabled}
            hostedServer={hostedProvisioning.server}
            hostedLoading={hostedProvisioning.isLoading}
            hostedBusy={hostedProvisioning.isBusy}
            isAutoProvisioning={hostedProvisioning.isAutoProvisioning}
            provisionError={hostedProvisioning.provisionError}
            onRetryProvision={hostedProvisioning.retryProvision}
            onStartHostedServer={hostedProvisioning.startServer}
            onTouchHostedServer={hostedProvisioning.touchServer}
            bootstrapPassword={hostedProvisioning.bootstrapPassword}
            onClearBootstrapPassword={hostedProvisioning.clearBootstrapPassword}
          />
        )}

        {!isJourneyVisible && (
          <>
            {showResumeBanner && (
              <JourneyResumeBanner
                mode={journey.status === 'not-started' ? 'start' : 'resume'}
                onResume={() =>
                  updateJourney({
                    status: 'guided',
                    step: journey.status === 'not-started' ? 1 : journey.step,
                    hostingChoice: 'managed'
                  })
                }
                onDismiss={() => updateJourney({ status: 'completed' })}
              />
            )}

            <ToolSelector 
              activeConverter={activeConverter}
              onConverterChange={handleConverterChange}
              activeServer={serverManagement.getActiveServer()}
              onAccountSelect={handleAccountSelect}
              onAccountDeselect={handleAccountDeselect}
              accountConfig={accountConfig}
              onShowAccountSettings={handleShowAccountSettings}
              onLinkAccount={handleShowAccountSettings}
              user={user}
            />

            {activeConverter && (
            <ConverterConfiguration
              activeConverter={activeConverter}
              startDate={startDate}
              onStartDateChange={setStartDate}
              targetCurrency={targetCurrency}
              onTargetCurrencyChange={setTargetCurrency}
              sourceCurrency={sourceCurrency}
              onSourceCurrencyChange={setSourceCurrency}
              ramiTargetCurrency={ramiTargetCurrency}
              onRamiTargetCurrencyChange={setRamiTargetCurrency}
              budgetCurrency={serverManagement.getActiveServer()?.defaultBudgetCurrency}
              enableCategorization={enableCategorization}
              onCategorizationChange={setEnableCategorization}
              categorizationConfig={categorizationConfig}
              servers={serverManagement.savedServers}
              user={user}
              onShowAuthModal={handleShowAuthModal}
              hasActiveSubscription={subscription.hasActiveSubscription}
              onShowSubscriptionModal={handleShowSubscriptionModal}
              isPaywallEnabled={isFeatureEnabled('ENABLE_PAYWALL')}
              onShowBetaModal={handleShowBetaModal}
            />
            )}

            <FileConverter
              activeConverter={activeConverter}
              selectedAccountId={selectedAccountId}
              isLoading={isLoading}
              error={error}
              result={result}
              previewTransactions={previewTransactions}
              targetCurrency={effectiveTargetCurrency}
              importResult={importFlowHook.importResult}
              isDragOver={isDragOver}
              onFileUpload={handleFileUpload}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDownload={handleDownload}
              onReset={handleReset}
              onImport={startImportFlow}
              onToggleTransaction={handleToggleTransaction}
              onSelectAllTransactions={handleSelectAllTransactions}
            />
          </>
        )}

        <ImportModal
          isVisible={importFlowHook.showImportModal}
          onClose={importFlowHook.closeImportModal}
          importFlow={importFlowHook.importFlow}
          isLoading={isLoading}
          error={error}
          savedServers={serverManagement.savedServers}
          result={result}
          selectedTransactionCount={selectedTransactionCount}
          conversionCurrency={effectiveTargetCurrency}
          budgetCurrency={serverManagement.getActiveServer()?.defaultBudgetCurrency}
          isImporting={importFlowHook.isImporting}
          importResult={importFlowHook.importResult}
          overspendLoading={importFlowHook.overspendLoading}
          hasOverspending={(importFlowHook.overspendSuggestion?.overspentCategories.length ?? 0) > 0}
          onFixOverspending={importFlowHook.openOverspendModal}
          newServerForm={newServerForm}
          onNewServerFormChange={(changes) => setNewServerForm(prev => ({ ...prev, ...changes }))}
          onServerSelection={handleServerSelection}
          onCreateServer={handleCreateServer}
          onBudgetSelection={handleBudgetSelection}
          onImportFlowChange={importFlowHook.updateImportFlow}
          onExecuteImport={executeImport}
          onRemoveServer={serverManagement.removeServer}
          onRemoveBudgetPassword={serverManagement.removeBudgetPassword}
          onShowServerManagement={() => {
            setShowServerManagement(true);
            importFlowHook.closeImportModal();
          }}
        />

        {importFlowHook.importFlow.selectedServer && importFlowHook.importFlow.selectedBudgetId && (
          <OverspendCoverageModal
            isVisible={importFlowHook.showOverspendModal}
            onClose={importFlowHook.closeOverspendModal}
            server={importFlowHook.importFlow.selectedServer}
            budgetId={importFlowHook.importFlow.selectedBudgetId}
            budgetPassword={importFlowHook.importFlow.budgetPassword}
            currency={serverManagement.getActiveServer()?.defaultBudgetCurrency || effectiveTargetCurrency}
            suggestion={importFlowHook.overspendSuggestion}
            loading={importFlowHook.overspendLoading}
            loadError={importFlowHook.overspendError}
            onSuggestionChange={importFlowHook.updateOverspendSuggestion}
          />
        )}

        <ServerManagementModal
          isVisible={showServerManagement}
          onClose={() => {
            setShowServerManagement(false);
            setServerConnectPrefill(null);
          }}
          user={user}
          savedServers={serverManagement.savedServers}
          activeServerId={serverManagement.activeServerId}
          onSaveServers={serverManagement.saveServers}
          onSetActiveServer={serverManagement.setActiveServer}
          onRemoveServer={serverManagement.removeServer}
          onRemoveBudgetPassword={serverManagement.removeBudgetPassword}
          onLoadBudgetsForServer={serverManagement.loadBudgetsForServer}
          onValidateBudgetAccess={serverManagement.validateBudgetAccess}
          connectPrefill={serverConnectPrefill}
          requireDefaultBudget={false}
        />

        <AuthModal
          isVisible={showAuthModal}
          onClose={handleCloseAuthModal}
          initialMode={authMode}
        />

        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={handleCloseSubscriptionModal}
          onSuccess={() => {
            handleCloseSubscriptionModal();
            subscription.refetch();
          }}
        />

        <BetaModeModal
          isVisible={showBetaModal}
          onClose={handleCloseBetaModal}
          onContinue={handleContinueToFeature}
          featureName={betaModalFeature}
        />

        <AccountSettingsModal
          isVisible={showAccountSettingsModal}
          onClose={handleCloseAccountSettingsModal}
          account={selectedAccountForSettings}
          accountConfig={accountConfig}
          onConverterChange={handleConverterChange}
          onUnlinkAccount={handleUnlinkAccount}
        />

      </main>
    </div>
  );
}

export default App; 